import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';
import { AiButton } from '../../../web/components-v2/AiButton';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import type { MyCoachingAdherence } from '../../../queries/AIConversation/useMyCoachingAdherenceQuery';
import { IconSparkles } from '../ui/icons';
import { MetricsStrip } from './MetricsStrip';
import type { DialMilestone } from './useDialMilestoneTrigger';

export type PacingMilestoneDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	milestone: DialMilestone | null;
	onGetFeedback?: () => void;
	/** The user's own coaching adherence for today; the breakdown is left out while it's unavailable. */
	adherence?: MyCoachingAdherence | null;
};

export const PacingMilestoneDialog: FC<PacingMilestoneDialogProps> = ({
	open,
	onOpenChange,
	milestone,
	onGetFeedback,
	adherence,
}) => {
	if (!milestone) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<div className={css(styles.iconRow)}>
						<span className={css(styles.iconBadge)}>
							<IconSparkles size={18} sw={2} />
						</span>
					</div>
					<DialogTitle>{milestone.dialsToday} dials — keep going.</DialogTitle>
					<DialogDescription>Here's where you are today.</DialogDescription>
				</DialogHeader>
				<DialogBody>
					<MetricsStrip variant='modal' input={{ ...milestone, adherence: adherence?.rep }} />
					{adherence && <CoachingBreakdown adherence={adherence} />}
				</DialogBody>
				<DialogFooter>
					{onGetFeedback && (
						<AiButton type='button' onClick={onGetFeedback} title='Get feedback' styles={[styles.aiFooterBtn]} />
					)}
					<Button variant='primary' size='lg' onClick={() => onOpenChange(false)}>
						Keep going
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

/** The strip shows today's share; this breaks today's calls down. */
const CoachingBreakdown: FC<{ adherence: MyCoachingAdherence }> = ({ adherence }) => {
	const { rep, followedScore } = adherence;
	return (
		<div className={css(styles.coaching)}>
			<span className={css(styles.coachingTitle)}>Coaching today</span>
			<ul className={css(styles.coachingList)}>
				<li>
					{`${rep.followedCalls} of ${rep.pastHookCalls} calls past the hook followed the coach (${followedScore}+)`}
				</li>
				<li>{`${rep.bookedFollowedCalls} of ${rep.bookedCalls} bookings came on followed calls`}</li>
				<li>{`${rep.hookRejectedFollowedCalls} of ${rep.hookRejectedCalls} hook-rejected calls still followed the coach`}</li>
			</ul>
		</div>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 460,
	},
	coaching: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		marginTop: 16,
		paddingTop: 16,
		borderTop: '1px solid var(--border)',
	},
	coachingTitle: {
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--ink-muted)',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	coachingList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		margin: 0,
		paddingLeft: 16,
		fontSize: 13,
		color: 'var(--ink)',
	},
	// Same height and side padding as the size='lg' "Keep going" Button beside it.
	aiFooterBtn: {
		height: 40,
		paddingLeft: 24,
		paddingRight: 24,
	},
	iconRow: {
		display: 'flex',
		justifyContent: 'center',
		marginBottom: 8,
	},
	iconBadge: {
		width: 44,
		height: 44,
		borderRadius: 12,
		background: 'var(--accent-soft)',
		color: 'var(--accent-ink)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});
